/**
 * AddNoteModal - Modal for coaches to add notes to clients
 *
 * Refactored: useBodyScrollLock, ESC key, touch-friendly, mobile bottom sheet
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  MessageSquare,
  Calendar,
  Pin,
  Loader2,
  Send,
} from 'lucide-react';
import { useCoachStore, NOTE_TYPES } from '../../stores/coachStore';
import { format } from 'date-fns';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const AddNoteModal = ({ clientId }) => {
  const {
    isAddNoteModalOpen,
    closeAddNoteModal,
    noteContext,
    addNote,
  } = useCoachStore();

  const [formData, setFormData] = useState({
    noteType: 'feedback',
    content: '',
    workoutDate: '',
    isPinned: false,
  });
  const [contextType, setContextType] = useState('general'); // 'general' | 'date'
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Body scroll lock (iOS-safe)
  useBodyScrollLock(isAddNoteModalOpen);

  // Memoized close handler for ESC key
  const handleClose = useCallback(() => {
    closeAddNoteModal();
  }, [closeAddNoteModal]);

  // ESC key to close
  useEffect(() => {
    if (!isAddNoteModalOpen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isAddNoteModalOpen, handleClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isAddNoteModalOpen) {
      setFormData({
        noteType: 'feedback',
        content: '',
        workoutDate: noteContext?.workoutDate || format(new Date(), 'yyyy-MM-dd'),
        isPinned: false,
      });
      setContextType(noteContext?.workoutDate ? 'date' : 'general');
    }
  }, [isAddNoteModalOpen, noteContext]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.content.trim()) return;

    setIsSubmitting(true);
    try {
      await addNote(clientId || noteContext?.clientId, {
        noteType: formData.noteType,
        content: formData.content.trim(),
        workoutDate: contextType === 'date' ? formData.workoutDate : null,
        workoutId: noteContext?.workoutId || null,
        isPinned: formData.isPinned,
      });
    } catch (error) {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAddNoteModalOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && handleClose()}
      >
        {/* Modal */}
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative bg-gray-900 border-t sm:border border-gray-800 rounded-t-2xl sm:rounded-xl w-full sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Mobile drag indicator */}
          <div className="sm:hidden flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-gray-700 rounded-full" />
          </div>

          {/* Header */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-800 flex items-center justify-between">
            <h2 className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-orange-500" />
              Not Ekle
            </h2>
            <button
              onClick={handleClose}
              className="p-2.5 hover:bg-gray-800 active:bg-gray-700 rounded-lg transition-colors min-h-touch min-w-touch flex items-center justify-center"
              aria-label="Kapat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
            {/* Note Type - touch-friendly */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Not Türü
              </label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(NOTE_TYPES).map(([key, type]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, noteType: key }))}
                    className={`p-3 min-h-touch rounded-lg border text-left transition-all active:scale-[0.98] ${
                      formData.noteType === key
                        ? 'bg-orange-500/20 border-orange-500'
                        : 'bg-gray-800/50 border-gray-700 hover:border-gray-600 active:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{type.emoji}</span>
                      <span className="text-sm font-medium">{type.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Context Type - touch-friendly */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Bağlam
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setContextType('general')}
                  className={`flex-1 py-3 min-h-touch px-3 rounded-lg border text-sm transition-all active:scale-[0.98] ${
                    contextType === 'general'
                      ? 'bg-orange-500/20 border-orange-500 text-orange-400'
                      : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600 active:bg-gray-700'
                  }`}
                >
                  Genel
                </button>
                <button
                  type="button"
                  onClick={() => setContextType('date')}
                  className={`flex-1 py-3 min-h-touch px-3 rounded-lg border text-sm transition-all active:scale-[0.98] ${
                    contextType === 'date'
                      ? 'bg-orange-500/20 border-orange-500 text-orange-400'
                      : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600 active:bg-gray-700'
                  }`}
                >
                  Belirli Gün
                </button>
              </div>
            </div>

            {/* Date Picker (if date context) */}
            {contextType === 'date' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Tarih
                </label>
                <input
                  type="date"
                  value={formData.workoutDate}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, workoutDate: e.target.value }))
                  }
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors"
                />
              </div>
            )}

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Notunuz
              </label>
              <textarea
                value={formData.content}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, content: e.target.value }))
                }
                placeholder="Öğrencinize notunuzu yazın..."
                rows={4}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors resize-none"
                maxLength={1000}
              />
              <p className="text-xs text-gray-500 mt-1 text-right">
                {formData.content.length}/1000
              </p>
            </div>

            {/* Pin Toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={formData.isPinned}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, isPinned: e.target.checked }))
                  }
                  className="sr-only"
                />
                <div
                  className={`w-10 h-6 rounded-full transition-colors ${
                    formData.isPinned ? 'bg-orange-500' : 'bg-gray-700'
                  }`}
                >
                  <div
                    className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform mt-1 ${
                      formData.isPinned ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Pin className={`w-4 h-4 ${formData.isPinned ? 'text-orange-400' : 'text-gray-500'}`} />
                <span className="text-sm">Sabitlenmiş olarak işaretle</span>
              </div>
            </label>
          </form>

          {/* Footer - with safe area padding */}
          <div className="px-4 sm:px-6 py-4 border-t border-gray-800 flex justify-end gap-3 pb-safe-area">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2.5 min-h-touch text-gray-400 hover:text-white hover:bg-gray-800 active:bg-gray-700 rounded-lg transition-colors"
            >
              İptal
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!formData.content.trim() || isSubmitting}
              className="flex items-center gap-2 px-4 py-2.5 min-h-touch bg-orange-600 hover:bg-orange-500 active:bg-orange-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Kaydet
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default AddNoteModal;
